import type { QuestionnaireItem } from "@/rtkQuery/features/models/questionnaires.model";
import { z } from "zod";

const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

export function generateZodSchemaFromQuestion(question?: QuestionnaireItem): z.ZodType<any> {
    if (!question) return z.object({});

    const { ResponseOptions = [], MinSelectedAnswers, MaxSelectedAnswers, HealthQuestionType } = question;

    if (ResponseOptions.length === 0 && HealthQuestionType === 6) {
        return z
            .object({
                email: z
                    .string()
                    .nonempty("Email is required")
                    .email("Invalid email address")
                    .transform(val => val.toLowerCase().trim())
                    .refine(val => val.length <= 320, "Email address is too long")
                    .refine(val => {
                        const domain = val.split('@')[1];
                        return domain && domain.includes('.') && domain.length >= 4;
                    }, "Invalid email domain"),


                password: z
                    .string()
                    .nonempty("Password is required")
                    .min(8, "Password must be at least 8 characters long.")
                    .regex(
                        strongPasswordRegex,
                        "Password must include at least one uppercase letter, one lowercase letter, one number, and one special character."
                    ),

                confirmPassword: z
                    .string()
                    .nonempty("Confirm password is required")
                    .min(8, "Confirm password must be at least 8 characters long."),
            })
            .refine((data) => data.password === data.confirmPassword, {
                message: "Passwords do not match.",
                path: ["confirmPassword"],
            });

    }

    const labels = ResponseOptions.map((opt) => opt.Label);

    if (MaxSelectedAnswers === 1) {
        return z.object({
            selected: z
                .string()
                .min(1, "Please select an option.")
                .refine((val) => labels.includes(val), "Invalid option."),
        });
    }

    return z.object({
        selected: z
            .array(z.string())
            .min(MinSelectedAnswers || 1, `Select at least ${MinSelectedAnswers} option(s).`)
            .max(MaxSelectedAnswers || labels.length, `Select at most ${MaxSelectedAnswers} option(s).`)
            .refine((vals) => vals.every((v) => labels.includes(v)), "One or more options are invalid."),
    });
}

export function getDefaultValuesForQuestion(question?: QuestionnaireItem): any {
    if (!question) return {};

    if (question.ResponseOptions.length === 0 && question.HealthQuestionType === 6) {
        return {
            email: "",
            password: "",
            confirmPassword: "",
        };
    }

    if (question.MaxSelectedAnswers > 1) {
        return { selected: [] };
    }

    return { selected: "" };
}
